import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import SpaceBackground from '../components/SpaceBackground';
import {
  ArrowRight,
  CheckCircle,
  Menu,
  X,
  Target,
  Lightbulb,
  TrendingUp,
  BarChart,
  Send,
  Mail,
  Phone,
  MapPin,
  Globe
} from 'lucide-react';

const Contact = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.fullName,
          company: formData.company,
          email: formData.email,
          phone: formData.phone,
          message: formData.message
        })
      });
      
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ fullName: '', company: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const processSteps = [
    { icon: Target, number: '01', title: 'Discover & Align', desc: 'Clarify goals, constraints, and where execution creates leverage.' },
    { icon: Lightbulb, number: '02', title: 'Co-design & Prototype', desc: 'Build pilots, roadmaps, and early wins with real-world fit.' },
    { icon: TrendingUp, number: '03', title: 'Execute & Scale', desc: 'Deliver with speed and precision using embedded local teams.' },
    { icon: BarChart, number: '04', title: 'Optimise & Evolve', desc: 'Stay engaged to refine performance and extend long-term impact.' }
  ];

  // Responsive grid columns
  const getGridColumns = () => {
    if (isMobile) return '1fr';
    if (isTablet) return 'repeat(2, 1fr)';
    return 'repeat(4, 1fr)';
  };

  return (
    <div className="landing-page" data-testid="contact-page">
      <SpaceBackground />
      
      <div className="page-content">
        {/* Header */}
        <header className={`header ${isScrolled ? 'scrolled' : ''}`} data-testid="navbar">
          <div className="header-content">
            <Link to="/" className="logo-container">
              <img 
                src="/moonrock-logo-light.png" 
                alt="MoonRock Research & Development" 
                className="navbar-logo-image"
              />
            </Link>
            
            {/* Desktop Nav */}
            <nav className="nav-menu" data-testid="nav-menu">
              <Link to="/who-we-are" className="nav-link-page">Who We Are</Link>
              <a href="/#how-we-work">How We Work</a>
              <a href="/#industries">Focus</a>
              <Link to="/projects" className="nav-link-page">Projects</Link>
            </nav>
            
            {/* CTA Button */}
            <div className="nav-cta-wrapper">
              <Link to="/contact">
                <Button className="nav-cta" data-testid="nav-cta">Partner With Us</Button>
              </Link>
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              className="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`} data-testid="mobile-menu">
            <nav className="mobile-nav-links">
              <Link 
                to="/who-we-are"
                className="nav-link-page"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Who We Are
              </Link>
              <a 
                href="/#how-we-work"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                How We Work
              </a>
              <a 
                href="/#industries"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Focus
              </a>
              <Link 
                to="/projects"
                className="nav-link-page"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Projects
              </Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="mobile-nav-cta">Partner With Us</Button>
              </Link>
            </nav>
          </div>
        </header>

        <div style={{ paddingTop: isMobile ? '20px' : '60px' }}>
        
        {/* Our Process Section */}
        <section style={{ 
          padding: isMobile ? '40px 16px 60px' : '60px 48px 80px',
          maxWidth: '1300px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1
        }} data-testid="contact-process">
          
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '40px' : '60px' }}>
            <span style={{ 
              display: 'inline-block', 
              fontSize: '0.8rem', 
              fontWeight: 700, 
              textTransform: 'uppercase', 
              letterSpacing: '0.2em', 
              color: '#CBB8FF', 
              marginBottom: '20px', 
              padding: '10px 24px', 
              background: 'rgba(203,184,255,0.1)', 
              border: '1px solid rgba(203,184,255,0.2)', 
              borderRadius: '100px' 
            }}>Our Process</span>
            <h1 style={{ 
              fontSize: isMobile ? '1.75rem' : 'clamp(2rem, 5vw, 3rem)', 
              fontWeight: 800, 
              color: '#FFFFFF', 
              margin: '0 0 20px 0', 
              letterSpacing: '-0.02em',
              lineHeight: 1.2
            }}>A clear path from opportunity to scale.</h1>
            <p style={{ 
              fontSize: isMobile ? '1rem' : '1.15rem', 
              color: '#A8A0B8', 
              maxWidth: '650px', 
              margin: '0 auto', 
              lineHeight: 1.7 
            }}>From discovery to delivery, we work with you at every step.</p>
          </div>
          
          {/* Process Steps Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: getGridColumns(),
            gap: isMobile ? '16px' : '24px'
          }}>
            {processSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div key={index} style={{
                  background: 'rgba(20, 15, 40, 0.6)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: isMobile ? '20px' : '24px',
                  padding: isMobile ? '28px 20px' : '40px 28px',
                  textAlign: 'center',
                  transition: 'all 0.3s ease'
                }}>
                  <div style={{
                    width: isMobile ? '56px' : '64px',
                    height: isMobile ? '56px' : '64px',
                    background: 'rgba(124, 92, 255, 0.15)',
                    border: '1px solid rgba(124, 92, 255, 0.3)',
                    borderRadius: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    color: '#CBB8FF'
                  }}>
                    <IconComponent size={isMobile ? 24 : 28} />
                  </div>
                  <span style={{
                    display: 'block',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: '#7C5CFF',
                    letterSpacing: '0.1em',
                    marginBottom: '12px'
                  }}>{step.number}</span>
                  <h3 style={{
                    fontSize: isMobile ? '1.1rem' : '1.2rem',
                    fontWeight: 700,
                    color: '#FFFFFF',
                    margin: '0 0 14px 0'
                  }}>{step.title}</h3>
                  <p style={{
                    fontSize: isMobile ? '0.9rem' : '0.95rem',
                    color: '#B8B0C8',
                    lineHeight: 1.7,
                    margin: 0
                  }}>{step.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Contact Form Section */}
        <section style={{ 
          padding: isMobile ? '40px 16px 80px' : '60px 48px 120px',
          maxWidth: '900px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1
        }} data-testid="contact-form-section">
          
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '48px' }}>
            <h2 style={{
              fontSize: isMobile ? '1.75rem' : 'clamp(2rem, 4vw, 2.5rem)',
              fontWeight: 700,
              color: '#FFFFFF',
              margin: '0 0 16px 0',
              letterSpacing: '-0.02em'
            }}>Get in Touch</h2>
            <p style={{
              fontSize: isMobile ? '1rem' : '1.1rem',
              color: '#A8A0B8',
              lineHeight: 1.7,
              margin: 0
            }}>Tell us about your project and let's start a conversation.</p>
          </div>

          {/* Form Card */}
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(124, 92, 255, 0.08) 0%, rgba(20, 15, 40, 0.8) 100%)', 
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(124, 92, 255, 0.15)', 
            borderRadius: isMobile ? '24px' : '28px',
            padding: isMobile ? '28px 20px' : '48px'
          }}>
            {isSubmitted ? (
              <div style={{ textAlign: 'center', padding: isMobile ? '20px 0' : '40px 0' }} data-testid="contact-success">
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: 'rgba(52, 211, 153, 0.15)', 
                  border: '2px solid rgba(52, 211, 153, 0.3)', 
                  borderRadius: '50%', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  margin: '0 auto 28px',
                  color: '#34D399'
                }}>
                  <CheckCircle size={40} />
                </div>
                <h2 style={{ 
                  fontSize: '1.75rem', 
                  fontWeight: 700, 
                  color: '#FFFFFF', 
                  margin: '0 0 12px 0' 
                }}>Message Sent!</h2>
                <p style={{ 
                  fontSize: '1.05rem', 
                  color: '#A8A0B8', 
                  margin: '0 0 32px 0', 
                  lineHeight: 1.7 
                }}>Thank you for reaching out. Our team will get back to you within 24-48 hours.</p>
                <Button 
                  onClick={() => setIsSubmitted(false)} 
                  style={{
                    background: 'linear-gradient(135deg, #7C5CFF 0%, #5A3FD6 100%)',
                    border: 'none',
                    padding: '14px 28px',
                    fontWeight: 600,
                    borderRadius: '14px',
                    fontSize: '1rem'
                  }}
                  data-testid="send-another-btn"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                {/* Two column row */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: isMobile ? '16px' : '24px',
                  marginBottom: isMobile ? '16px' : '24px'
                }}>
                  <div>
                    <Label htmlFor="input-name" style={{ 
                      color: '#E0DCE8', 
                      marginBottom: '8px', 
                      display: 'block',
                      fontSize: '0.95rem'
                    }}>
                      Full Name <span style={{ color: '#F472B6' }}>*</span>
                    </Label>
                    <Input
                      id="input-name"
                      name="fullName"
                      placeholder="Your full name"
                      value={formData.fullName}
                      onChange={handleChange}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: errors.fullName ? '1px solid #F87171' : '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        borderRadius: '12px',
                        padding: '14px 18px',
                        fontSize: '1rem'
                      }}
                      data-testid="input-name"
                    />
                    {errors.fullName && <span style={{ color: '#F87171', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>{errors.fullName}</span>}
                  </div>
                  
                  <div>
                    <Label htmlFor="input-company" style={{ 
                      color: '#E0DCE8', 
                      marginBottom: '8px', 
                      display: 'block',
                      fontSize: '0.95rem'
                    }}>
                      Company <span style={{ color: '#A8A0B8', fontWeight: 400 }}>(optional)</span>
                    </Label>
                    <Input
                      id="input-company"
                      name="company"
                      placeholder="Your company name"
                      value={formData.company}
                      onChange={handleChange}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        borderRadius: '12px',
                        padding: '14px 18px',
                        fontSize: '1rem'
                      }}
                      data-testid="input-company"
                    />
                  </div>
                </div>

                {/* Two column row */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                  gap: isMobile ? '16px' : '24px',
                  marginBottom: isMobile ? '16px' : '24px'
                }}>
                  <div>
                    <Label htmlFor="input-email" style={{ 
                      color: '#E0DCE8', 
                      marginBottom: '8px', 
                      display: 'block',
                      fontSize: '0.95rem'
                    }}>
                      Email <span style={{ color: '#F472B6' }}>*</span>
                    </Label>
                    <Input
                      id="input-email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: errors.email ? '1px solid #F87171' : '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        borderRadius: '12px',
                        padding: '14px 18px',
                        fontSize: '1rem'
                      }}
                      data-testid="input-email"
                    />
                    {errors.email && <span style={{ color: '#F87171', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>{errors.email}</span>}
                  </div>
                  
                  <div>
                    <Label htmlFor="input-phone" style={{ 
                      color: '#E0DCE8', 
                      marginBottom: '8px', 
                      display: 'block',
                      fontSize: '0.95rem'
                    }}>
                      Phone <span style={{ color: '#A8A0B8', fontWeight: 400 }}>(optional)</span>
                    </Label>
                    <Input
                      id="input-phone"
                      name="phone"
                      type="tel"
                      placeholder="+968 XXXX XXXX"
                      value={formData.phone}
                      onChange={handleChange}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        borderRadius: '12px',
                        padding: '14px 18px',
                        fontSize: '1rem'
                      }}
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                {/* Message field */}
                <div style={{ marginBottom: isMobile ? '24px' : '32px' }}>
                  <Label htmlFor="input-message" style={{ 
                    color: '#E0DCE8', 
                    marginBottom: '8px', 
                    display: 'block',
                    fontSize: '0.95rem'
                  }}>
                    Message <span style={{ color: '#F472B6' }}>*</span>
                  </Label>
                  <Textarea
                    id="input-message"
                    name="message"
                    placeholder="Tell us about your project, timeline, and goals..."
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: errors.message ? '1px solid #F87171' : '1px solid rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      borderRadius: '12px',
                      padding: '14px 18px',
                      fontSize: '1rem',
                      resize: 'vertical',
                      minHeight: '140px'
                    }}
                    data-testid="input-message"
                  />
                  {errors.message && <span style={{ color: '#F87171', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>{errors.message}</span>}
                </div>

                {/* Submit button */}
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #7C5CFF 0%, #5A3FD6 100%)',
                    border: 'none',
                    padding: '16px 32px',
                    fontWeight: 600,
                    borderRadius: '14px',
                    fontSize: '1.05rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  data-testid="submit-btn"
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      Send Message
                      <Send size={18} />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </section>

        {/* Footer */}
        <footer className="footer" data-testid="footer">
          <div className="footer-glass">
            <div className="footer-content">
              <div className="footer-grid">
                {/* Brand Block */}
                <div className="footer-brand">
                  <div className="footer-logo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <img 
                      src="https://customer-assets.emergentagent.com/job_52aedb38-24ea-4ee4-8eda-846c0d3c3b94/artifacts/8gbuyg09_Full%20Logo%20Light%20TP%20space%20%281%29.png" 
                      alt="MoonRock R&D" 
                      style={{ height: '56px', width: 'auto', marginBottom: '14px', objectFit: 'contain' }}
                    />
                    <p className="footer-tagline" style={{ margin: 0, paddingLeft: '10px' }}>We Shape What Lasts.</p>
                  </div>
                </div>
                
                {/* Quick Links */}
                <div className="footer-links">
                  <h4 className="footer-heading">Quick Links</h4>
                  <ul>
                    <li><Link to="/who-we-are">Who We Are</Link></li>
                    <li><a href="/#how-we-work">How We Work</a></li>
                    <li><a href="/#industries">Focus</a></li>
                    <li><Link to="/projects">Projects</Link></li>
                  </ul>
                </div>
                
                {/* Contact */}
                <div className="footer-contact">
                  <h4 className="footer-heading">Contact</h4>
                  <div className="contact-info">
                    <div className="contact-item">
                      <MapPin className="contact-icon" />
                      <span>Suite 403, Bldg. 393, 18th November St, Muscat</span>
                    </div>
                    <div className="contact-item">
                      <Mail className="contact-icon" />
                      <a href="mailto:admin@moonrock.om">admin@moonrock.om</a>
                    </div>
                    <div className="contact-item">
                      <Phone className="contact-icon" />
                      <a href="tel:+96896607256">+968 9660 7256</a>
                    </div>
                    <div className="contact-item">
                      <Globe className="contact-icon" />
                      <a href="https://moonrock.om" target="_blank" rel="noopener noreferrer">moonrock.om</a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Bottom Row */}
              <div className="footer-divider"></div>
              <div className="footer-bottom">
                <p className="footer-copyright">© 2026 MoonRock R&D. All rights reserved.</p>
              </div>
            </div>
          </div>
        </footer>
        </div>
      </div>
    </div>
  );
};

export default Contact;
